import { useEffect, useState } from 'react';
import _ from 'lodash';
import { UserActions } from 'BMapsCmds';
import { useSelector } from 'BMapsRedux';
import { setSidePanelWidth } from 'BMapsRedux/projectState/access';
import { useColorSchemeInfo } from 'BMapsRedux/prefs/access';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { SidePanelContainer } from 'BMapsUI/info_display/SidePanel';
import TabController from 'BMapsUI/common/TabController';
import tabs from './Tabs';

const TABLE_ID = 'mdTable';
const DEFAULT_WIDTH = 780;

/**
 * The Molecular Dynamics side panel: submit an MD job with STORMM or GROMACS
 * (New Job), watch and cancel jobs (Jobs), and view pose stability,
 * interactions, plots and final poses (Results). Structured like the Free
 * Energy panel.
 */
export function MdSidePanel({
    open, handleClose, tabId='new_md_tab', initData={},
}) {
    const tableState = useSelector((state) => state.projectState.tableState[TABLE_ID]) || {};
    const { sidePanelWidth = DEFAULT_WIDTH } = tableState;

    const { textCss } = useColorSchemeInfo();

    const [width, setWidth] = useState(sidePanelWidth);
    const [highlightedJobId, setHighlightedJobId] = useState(null);
    const [resizing, setResizing] = useState(false);

    function onMouseDown(e) {
        e.preventDefault();
        e.stopPropagation();
        setResizing(true);
    }

    function setTabId(tab) {
        openMdSidePanel(tab, initData);
    }

    useEffect(() => {
        // This 0 debounce prevents a "too many renders" error that can happen when resizing quickly
        const debouncedSetWidth = _.debounce((newWidth) => setWidth(newWidth), 0);
        function onMouseUp() {
            setResizing(false);
            setSidePanelWidth(width, TABLE_ID);
            EventBroker.publish('resize');
            debouncedSetWidth.cancel();
        }
        function onMouseMove(e) {
            e.preventDefault();
            e.stopPropagation();
            const newWidth = e.clientX - 10;
            if (newWidth > 20) {
                debouncedSetWidth(newWidth);
            }
        }
        if (resizing) {
            document.addEventListener('mouseup', onMouseUp);
            document.addEventListener('mousemove', onMouseMove);
            return () => {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
                debouncedSetWidth.cancel();
            };
        }
        return undefined;
    }, [resizing]);

    return (
        <div style={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
            <SidePanelContainer
                open={open}
                handleClose={handleClose}
                title="Molecular Dynamics"
                width={width}
                contentStyle={{
                    padding: '0.5rem',
                }}
            >
                <TabController
                    tabs={tabs.map((tab) => tab.forTabController(
                        {
                            initData,
                            highlightedJobId,
                            setHighlightedJobId,
                        }
                    ))}
                    selected={tabId}
                    setSelected={setTabId}
                    style={{
                        flex: 1,
                    }}
                />
            </SidePanelContainer>
            <div
                role="toolbar"
                onMouseDown={onMouseDown}
                style={{
                    cursor: 'ew-resize',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    marginRight: '.5rem',
                }}
            >
                <i className="fa fa-arrows-h" style={{ color: textCss }} />
            </div>
        </div>
    );
}

/**
 * Open (or re-target) the Molecular Dynamics side panel.
 * @param {'new_md_tab'|'md_jobs_tab'|'md_results_tab'} tabId
 * @param {{ selectedCompounds?: import('BMapsModel').Compound[] }} [initData]
 */
export function openMdSidePanel(tabId, initData) {
    const validTabId = tabs.find((t) => t.id === tabId)?.id || tabs[0].id;
    UserActions.OpenSidePanel('left', {
        pageId: 'molecular_dynamics',
        props: {
            initData,
            tabId: validTabId,
        },
        component: <MdSidePanel />,
        longLived: false,
    });
}

EventBroker.subscribe('askOpenMdPanel', (event, tabId) => openMdSidePanel(tabId));
